import { IonModal, useIonRouter, useIonViewWillEnter, useIonViewWillLeave } from "@ionic/react";
import { chevronDownOutline, chevronUpOutline, ellipsisHorizontal } from "ionicons/icons";
import dayjs from "dayjs";
import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Redirect, useParams } from "react-router-dom";
import Icon from "../../components/atoms/Icon";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../navigation/studyTabs";
import {
  deleteSuiviGroupMember,
  deleteSuiviMemberDeath,
  getSuiviGroupDetail,
  postSuiviGroupExit,
  postSuiviMemberAccessLink,
  postSuiviMemberDeath,
  postSuiviMemberExit,
  postSuiviReminderStamp,
  SuiviExitBody,
  SuiviFillSequence,
  SuiviGroupDetail,
  SuiviMemberDetail,
  SuiviSequenceBase,
} from "../../services/suivi.service";
import { getWelcomeInfo } from "../../services/weclome.service";
import { canManageDossiers } from "../../helper/familyComposition";
import { AddMemberModal } from "./DossierModals";
import { DeathModal, ExitModal, MemberActionsModal } from "./ExitModals";
import { AccessLinkModal } from "../../components/organisms/Invitation/InviteModals";
import useToast from "../../hooks/useToast";
import {
  ModalCard,
  ModalTitle,
  ModalDescription,
  ModalActions,
  ModalPrimaryButton,
  ModalQuietButton,
} from "../../components/atoms/ModalKit";
import { RoleEnum } from "../../types/invitation.types";
import CallSheet from "./CallSheet";
import {
  Chevron,
  DueChip,
  EmptyText,
  FillPill,
  FooterLine,
  ListCard,
  MemberInitial,
  OutlinePill,
  Page,
  QuietTag,
  Row,
  RowEnd,
  RowMain,
  RowSub,
  RowTitle,
  SectionLabel,
  StatusText,
  memberRoleOrdinalLabel,
} from "./suiviUi";

/**
 * Espace groupe (famille) — everything about one dossier. Layout contract:
 * Membres (call pills on members whose role collects a phone) →
 * « À remplir par vous » (warm CTA, straight into the runner, returns HERE) →
 * « Côté participants » (read-only status + relance tag + call) →
 * « Historique » folded (done ✓ / missed, never actionable).
 */
/* Tight stack for the future-window sentences (Page's 14px gap is too airy
   for what should read as one quiet block). */
const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const GroupSpace: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { groupKey } = useParams<{ groupKey: string }>();
  // Pushed URLs carry the key percent-encoded (external_id is free text);
  // react-router v5 hands the segment back raw, so decode here. SAFE decode:
  // a mangled deep link (dangling % escape) must fall through to the API's
  // uniform 404, not throw URIError during render.
  const rawGroupKey = useMemo(() => {
    try {
      return decodeURIComponent(groupKey);
    } catch {
      return groupKey;
    }
  }, [groupKey]);
  const { study, selectedRole } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();

  const [detail, setDetail] = useState<SuiviGroupDetail | null>(null);
  const [isError, setIsError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [callMember, setCallMember] = useState<SuiviMemberDetail | null>(null);
  // Dossier management (médecin authoritative: add always, remove only data-empty).
  const [canManage, setCanManage] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<SuiviMemberDetail | null>(null);
  const [removing, setRemoving] = useState(false);
  // Exit / décès: one chooser per member, then the matching form. exitTarget === null
  // with exitDossier true means "the whole dossier".
  const [actionsMember, setActionsMember] = useState<SuiviMemberDetail | null>(null);
  const [exitTarget, setExitTarget] = useState<SuiviMemberDetail | null>(null);
  const [exitDossier, setExitDossier] = useState(false);
  const [deathTarget, setDeathTarget] = useState<SuiviMemberDetail | null>(null);
  const [accessLinkTarget, setAccessLinkTarget] = useState<SuiviMemberDetail | null>(null);
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const present = useToast();

  // True only while this view is the active Ionic page — a late fetch response
  // must never rewrite the GLOBAL layout title of whatever page is now shown.
  const isActiveRef = useRef(false);
  useIonViewWillLeave(() => {
    isActiveRef.current = false;
  });

  const fetchDetail = useCallback((): Promise<void> => {
    if (!study?.id) return Promise.resolve();
    // Never fire the investigator-only request while the self-guard below is
    // redirecting a known non-investigator away.
    if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
      return Promise.resolve();
    }
    return getSuiviGroupDetail({ studyId: study.id, groupKey: rawGroupKey, lang: language })
      .then(({ data }) => {
        setIsError(false);
        setDetail(data);
        if (isActiveRef.current) {
          setResonanceLayout({
            title: data.external_id
              ? t("suivi_dossier", { code: data.external_id })
              : t("suivi_group_title"),
          });
        }
      })
      .catch(() => setIsError(true));
    // setResonanceLayout/t are stable enough for this page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study?.id, rawGroupKey, language, selectedRole]);

  // Refetch on every entry — including the return from the questionnaire runner,
  // so a just-filled row moves to Historique immediately (trampoline pattern:
  // useIonViewWillEnter registers once).
  const willEnterRef = useRef<(() => void) | undefined>(undefined);
  willEnterRef.current = () => {
    isActiveRef.current = true;
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      // Re-entering with data already loaded keeps the dossier title stable.
      title: detail?.external_id
        ? t("suivi_dossier", { code: detail.external_id })
        : "suivi_group_title",
      welcomePage: false,
      headerLeft: ["back"],
      headerBackFallbackPath: `/${study_slug}/tabs/suivi`,
    });
    fetchDetail();
  };
  useIonViewWillEnter(() => {
    willEnterRef.current?.();
  });

  // In-place language change (hamburger menu) never re-enters the view — refetch
  // so API-translated labels follow the UI language (fetchDetail deps include it).
  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // Same capability gate as SuiviHome: dossier management only where families
  // exist (≥1 data-only subject role in the cached welcome catalog). Fail-soft.
  useEffect(() => {
    let cancelled = false;
    if (!study_slug || !selectedRole) return undefined;
    getWelcomeInfo({ slug: study_slug, lang: language })
      .then(({ data: welcome }) => {
        if (!cancelled) setCanManage(canManageDossiers(selectedRole, welcome.roles));
      })
      .catch(() => {
        /* affordances simply stay hidden */
      });
    return () => {
      cancelled = true;
    };
  }, [study_slug, language, selectedRole]);

  if (selectedRole && selectedRole.name !== RoleEnum.INVESTIGATOR) {
    return <Redirect to={getDefaultTabPath(study_slug, selectedRole.name)} />;
  }

  const memberById = new Map(
    (detail?.members ?? []).map((member) => [member.user_id, member])
  );

  // Role label with sibling ordinal (« Enfant 2 ») — the only way to tell
  // same-role members apart before a fiche names them.
  const memberRoleLabel = (member: SuiviMemberDetail): string =>
    memberRoleOrdinalLabel(member, detail?.members ?? [], t("suivi_member_generic"));

  const memberDisplayName = (member: SuiviMemberDetail): string => {
    const name = [member.firstname, member.lastname].filter(Boolean).join(" ");
    // Identity fallback chain: ordinal role label leads, name is garnish once
    // an identity question collects it (« Enfant 2 — Léa »).
    const role = memberRoleLabel(member);
    return name ? `${role} — ${name}` : role;
  };

  // Sequence rows carry subject_user_id — label them with the live member
  // (ordinal + name) so « À remplir » rows say WHICH child they are about.
  // Fallback: role label from the row (member gone, e.g. history of a removed one).
  const labelForSubject = (
    subjectUserId: number,
    fallbackRoleLabel: string | null
  ): string | null => {
    const member = memberById.get(subjectUserId);
    return member ? memberDisplayName(member) : fallbackRoleLabel;
  };

  const openRunner = (row: SuiviFillSequence): void => {
    const next = row.components.find(
      (component) => component.questionnaire_id && !component.is_done
    );
    if (!next) return;
    ionRouter.push(
      `/${study_slug}/tabs/suivi/groups/${encodeURIComponent(rawGroupKey)}/sequences/${row.user_sequence_id}/components/${next.id}`,
      "forward",
      "push"
    );
  };

  const statusWords = (row: SuiviSequenceBase): ReactElement => (
    <StatusText $active={row.status === "IN_PROGRESS"}>
      {row.status === "IN_PROGRESS"
        ? t("suivi_status_in_progress", {
            answered: row.progress.answered,
            total: row.progress.total,
          })
        : t("suivi_status_not_started")}
    </StatusText>
  );

  // A member is contactable with a phone (call) or, failing that, an email.
  const isContactable = (member: SuiviMemberDetail): boolean =>
    member.has_phone || Boolean(member.email);
  const contactLabel = (member: SuiviMemberDetail): string =>
    member.has_phone ? t("suivi_call") : t("suivi_contact");

  const doneCount = (detail?.history ?? []).filter((entry) => entry.kind === "DONE").length;
  const missedCount = (detail?.history ?? []).filter((entry) => entry.kind === "EXPIRED").length;

  const handleMarkReminded = async (): Promise<void> => {
    if (!callMember || !study?.id) return;
    await postSuiviReminderStamp({
      studyId: study.id,
      groupKey: rawGroupKey,
      subjectUserId: callMember.user_id,
    });
    // Await the refetch: the sheet derives its "stamped today" state from the
    // fresh detail, so the button must stay disabled until it lands.
    await fetchDetail();
  };

  const handleRemoveMember = async (): Promise<void> => {
    if (!removeTarget || !study?.id) return;
    setRemoving(true);
    try {
      await deleteSuiviGroupMember({
        studyId: study.id,
        groupKey: rawGroupKey,
        memberUserId: removeTarget.user_id,
      });
      setRemoveTarget(null);
      present(t("suivi_remove_member_done"));
      await fetchDetail();
    } catch (e: any) {
      // 409 = data appeared since the page loaded (deletable is a snapshot).
      present(
        e?.response?.status === 409
          ? t("suivi_remove_member_blocked")
          : t("common_server_error_desc")
      );
      setRemoveTarget(null);
      await fetchDetail();
    } finally {
      setRemoving(false);
    }
  };

  // Exit and death share this tail: close the form, tell the investigator what happened,
  // then re-read. A dossier-wide exit is the one case that must NOT refetch in place —
  // every member is now inactive, so the group key stops resolving and the detail 404s.
  const finishAction = async (message: string, leaveGroup = false): Promise<void> => {
    setActionSaving(false);
    setActionError(null);
    setActionsMember(null);
    setExitTarget(null);
    setExitDossier(false);
    setDeathTarget(null);
    setAccessLinkTarget(null);
    present(message);
    if (leaveGroup) ionRouter.push(`/${study_slug}/tabs/suivi`, "back", "pop");
    else await fetchDetail();
  };

  const handleExit = async (payload: SuiviExitBody): Promise<void> => {
    if (!study?.id) return;
    setActionSaving(true);
    setActionError(null);
    try {
      if (exitDossier) {
        await postSuiviGroupExit({ studyId: study.id, groupKey: rawGroupKey }, payload);
        await finishAction(t("suivi_exit_dossier_done"), true);
      } else if (exitTarget) {
        await postSuiviMemberExit(
          { studyId: study.id, groupKey: rawGroupKey, memberUserId: exitTarget.user_id },
          payload
        );
        await finishAction(t("suivi_exit_member_done"));
      }
    } catch (e: any) {
      setActionSaving(false);
      setActionError(e?.response?.data?.message ?? t("common_server_error_desc"));
    }
  };

  const handleRecordDeath = async (payload: {
    death_date: string | null;
    note: string | null;
  }): Promise<void> => {
    if (!study?.id || !deathTarget) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviMemberDeath(
        { studyId: study.id, groupKey: rawGroupKey, memberUserId: deathTarget.user_id },
        payload
      );
      await finishAction(t("suivi_death_done"));
    } catch (e: any) {
      setActionSaving(false);
      setActionError(e?.response?.data?.message ?? t("common_server_error_desc"));
    }
  };

  const handleSendAccessLink = async ({ email }: { email: string | null }): Promise<void> => {
    if (!study?.id || !accessLinkTarget) return;
    setActionSaving(true);
    setActionError(null);
    try {
      await postSuiviMemberAccessLink(
        { studyId: study.id, groupKey: rawGroupKey, memberUserId: accessLinkTarget.user_id },
        { channel: "EMAIL", email, lang: language }
      );
      await finishAction(t("suivi_access_link_done"));
    } catch (e: any) {
      setActionSaving(false);
      // 409 = no contact for the channel, or the researcher configured no invitation
      // from this staff role to the member's role — both are actionable, so show the
      // server's own wording rather than a generic failure.
      setActionError(
        e?.response?.status === 409
          ? t("suivi_access_link_unavailable")
          : e?.response?.data?.message ?? t("common_server_error_desc")
      );
    }
  };

  const handleClearDeath = async (member: SuiviMemberDetail): Promise<void> => {
    if (!study?.id) return;
    try {
      await deleteSuiviMemberDeath({
        studyId: study.id,
        groupKey: rawGroupKey,
        memberUserId: member.user_id,
      });
      await finishAction(t("suivi_death_clear_done"));
    } catch {
      setActionsMember(null);
      present(t("common_server_error_desc"));
      await fetchDetail();
    }
  };

  return (
    <ResonanceLayout>
      {/* Single fragment child — ResonanceLayout's children prop is one element. */}
      <>
      <Page>
        {isError ? (
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : detail === null ? (
          <ResonanceLoader />
        ) : (
          <>
            <SectionLabel>{t("suivi_members")}</SectionLabel>
            <ListCard>
              {detail.members.map((member) => (
                <Row key={member.user_id}>
                  <MemberInitial>
                    {(member.role_label ?? "?").charAt(0).toUpperCase()}
                  </MemberInitial>
                  <RowMain>
                    <RowTitle style={{ fontSize: 14 }}>
                      {memberDisplayName(member)}
                    </RowTitle>
                    {/* Status first: an exited or deceased member must read as such
                        before anything else on the row. */}
                    {(member.exited ||
                      member.deceased ||
                      member.invited_at ||
                      member.last_reminder_stamp) && (
                      <RowSub>
                        {member.deceased && <QuietTag>{t("suivi_deceased_tag")}</QuietTag>}
                        {/* Only while it still matters: once they have registered, when
                            the link was sent is no longer the useful fact. */}
                        {member.invited_at && !member.registered && !member.exited && (
                          <QuietTag>
                            {t("suivi_invited_on", {
                              date: dayjs(member.invited_at).format("DD/MM"),
                            })}
                          </QuietTag>
                        )}
                        {member.exited && (
                          <QuietTag>
                            {member.exit_reason_code
                              ? t("suivi_exited_tag_reason", {
                                  reason: t(exitReasonLabelKey(member.exit_reason_code)),
                                })
                              : t("suivi_exited_tag")}
                          </QuietTag>
                        )}
                        {member.last_reminder_stamp && (
                          <QuietTag>
                            {t("suivi_reminded_on", {
                              date: dayjs(member.last_reminder_stamp.at).format("DD/MM"),
                            })}
                          </QuietTag>
                        )}
                      </RowSub>
                    )}
                  </RowMain>
                  {/* Someone who left the study is not someone to call. */}
                  {isContactable(member) && !member.exited && (
                    <OutlinePill type="button" onClick={() => setCallMember(member)}>
                      {contactLabel(member)}
                    </OutlinePill>
                  )}
                  {canManage && (
                    <RemoveButton
                      type="button"
                      aria-label={t("suivi_member_actions")}
                      onClick={() => setActionsMember(member)}
                    >
                      <Icon icon={ellipsisHorizontal} size="small" />
                    </RemoveButton>
                  )}
                </Row>
              ))}
            </ListCard>

            {canManage && (
              <AddMemberRow>
                <OutlinePill type="button" onClick={() => setAddOpen(true)}>
                  + {t("suivi_add_member_button")}
                </OutlinePill>
                {/* Dossier-wide, and only where a dossier exists: in subject grouping
                    mode there is nothing but the single member to withdraw. */}
                {detail.grouping_mode === "case" && detail.external_id && (
                  <OutlinePill
                    type="button"
                    onClick={() => {
                      setExitDossier(true);
                      setActionError(null);
                    }}
                  >
                    {t("suivi_exit_dossier_button")}
                  </OutlinePill>
                )}
              </AddMemberRow>
            )}

            {detail.to_fill.length > 0 && (
              <>
                <SectionLabel>
                  {t("suivi_to_fill")} ({detail.to_fill.length})
                </SectionLabel>
                <ListCard>
                  {detail.to_fill.map((row) => (
                    <Row key={row.user_sequence_id}>
                      <RowMain>
                        <RowTitle>{row.label ?? t("suivi_questionnaire")}</RowTitle>
                        <RowSub>
                          {labelForSubject(row.subject_user_id, row.subject_role_label) && (
                            <span>
                              {labelForSubject(row.subject_user_id, row.subject_role_label)}
                            </span>
                          )}
                          <DueChip endDate={row.end_date} t={t} />
                        </RowSub>
                      </RowMain>
                      {row.filled_by_colleague ? (
                        <QuietTag>{t("suivi_filled_by_colleague")}</QuietTag>
                      ) : (
                        <FillPill type="button" onClick={() => openRunner(row)}>
                          {row.progress.answered > 0
                            ? t("suivi_continue_progress", {
                                answered: row.progress.answered,
                                total: row.progress.total,
                              })
                            : t("suivi_fill")}
                        </FillPill>
                      )}
                    </Row>
                  ))}
                </ListCard>
              </>
            )}

            {detail.awaiting_subjects.length > 0 && (
              <>
                <SectionLabel>
                  {t("suivi_parents_side")} ({detail.awaiting_subjects.length})
                </SectionLabel>
                <ListCard>
                  {detail.awaiting_subjects.map((row) => {
                    const member = memberById.get(row.subject_user_id);
                    return (
                      <Row key={row.user_sequence_id}>
                        <RowMain>
                          <RowTitle>{row.label ?? t("suivi_questionnaire")}</RowTitle>
                          <RowSub>
                            {labelForSubject(row.subject_user_id, row.subject_role_label) && (
                              <span>
                                {labelForSubject(row.subject_user_id, row.subject_role_label)}
                              </span>
                            )}
                            {statusWords(row)}
                            {/* Per-child questionnaire: the PARENT must answer it,
                                not the child the row is about. */}
                            {row.answerer_user_id != null &&
                              labelForSubject(row.answerer_user_id, null) && (
                                <QuietTag>
                                  {t("suivi_to_answer_by", {
                                    who: labelForSubject(row.answerer_user_id, null),
                                  })}
                                </QuietTag>
                              )}
                            {member?.last_reminder_stamp && (
                              <QuietTag>
                                {t("suivi_reminded_on", {
                                  date: dayjs(member.last_reminder_stamp.at).format(
                                    "DD/MM"
                                  ),
                                })}
                              </QuietTag>
                            )}
                          </RowSub>
                        </RowMain>
                        <RowEnd>
                          <DueChip endDate={row.end_date} t={t} />
                          {member && isContactable(member) && (
                            <OutlinePill
                              type="button"
                              onClick={() => setCallMember(member)}
                            >
                              {contactLabel(member)}
                            </OutlinePill>
                          )}
                        </RowEnd>
                      </Row>
                    );
                  })}
                </ListCard>
              </>
            )}

            {detail.to_fill.length === 0 && detail.awaiting_subjects.length === 0 && (
              <EmptyText>{t("suivi_empty_group")} ✓</EmptyText>
            )}

            {detail.history.length > 0 && (
              <ListCard>
                <Row
                  as="button"
                  type="button"
                  $tappable
                  onClick={() => setHistoryOpen((open) => !open)}
                >
                  <RowMain>
                    <RowTitle style={{ fontWeight: 500, fontSize: 14 }}>
                      {t("suivi_history")}
                    </RowTitle>
                  </RowMain>
                  <RowSub as="span">
                    {doneCount > 0 && t("suivi_history_done", { count: doneCount })}
                    {doneCount > 0 && missedCount > 0 && " · "}
                    {missedCount > 0 && t("suivi_history_missed", { count: missedCount })}
                  </RowSub>
                  <Chevron as={Icon} icon={historyOpen ? chevronUpOutline : chevronDownOutline} size="small" />
                </Row>
                {historyOpen &&
                  detail.history.map((entry) => (
                    <Row key={entry.user_sequence_id}>
                      <RowMain>
                        <RowTitle style={{ fontWeight: 500 }}>
                          {entry.kind === "DONE" ? "✓ " : "— "}
                          {entry.label ?? t("suivi_questionnaire")}
                        </RowTitle>
                        <RowSub>
                          {labelForSubject(entry.subject_user_id, entry.subject_role_label) && (
                            <span>
                              {labelForSubject(entry.subject_user_id, entry.subject_role_label)}
                            </span>
                          )}
                          <span>
                            {entry.kind === "DONE"
                              ? t("suivi_done_on", {
                                  date: dayjs(entry.at).format("DD/MM/YYYY"),
                                })
                              : t("suivi_missed_on", {
                                  date: dayjs(entry.at).format("DD/MM/YYYY"),
                                })}
                          </span>
                          {/* Who produced this answer, so a per-child questionnaire does
                              not read as the child's own. A parent → their name; the
                              investigator viewing (ME) → « vous ». */}
                          {entry.answered_by === "ME" && (
                            <QuietTag>
                              {t("suivi_answered_by", { who: t("suivi_answered_by_you") })}
                            </QuietTag>
                          )}
                          {entry.answered_by === "FAMILY" &&
                            entry.answered_by_user_id != null &&
                            labelForSubject(entry.answered_by_user_id, null) && (
                              <QuietTag>
                                {t("suivi_answered_by", {
                                  who: labelForSubject(entry.answered_by_user_id, null),
                                })}
                              </QuietTag>
                            )}
                        </RowSub>
                      </RowMain>
                    </Row>
                  ))}
              </ListCard>
            )}

            {/* Future windows are sentences, never cards (design contract):
                the investigator's next fill first, then each member's own. */}
            {detail.next_windows.length > 0 && (
              <FooterStack>
                {detail.next_windows.map((window) => (
                  <FooterLine
                    key={`${window.kind}-${window.subject_user_id}-${window.sequence_id}`}
                  >
                    {window.kind === "FILL"
                      ? t("suivi_next_window_fill", {
                          label: window.label ?? t("suivi_questionnaire"),
                          date: dayjs(window.opens_at).format("DD/MM/YYYY"),
                        })
                      : t("suivi_next_window_subject", {
                          role:
                            labelForSubject(window.subject_user_id, window.subject_role_label)
                            ?? t("suivi_member_generic"),
                          label: window.label ?? t("suivi_questionnaire"),
                          date: dayjs(window.opens_at).format("DD/MM/YYYY"),
                        })}
                  </FooterLine>
                ))}
              </FooterStack>
            )}
          </>
        )}
      </Page>

      <CallSheet
        // LIVE member from the freshest detail (not the state snapshot) so the
        // sheet reflects the reminder stamp the moment the refetch lands.
        member={
          callMember
            ? memberById.get(callMember.user_id) ?? callMember
            : null
        }
        roleLabel={callMember ? memberRoleLabel(callMember) : undefined}
        pending={(detail?.awaiting_subjects ?? []).filter(
          (row) => row.subject_user_id === callMember?.user_id
        )}
        onClose={() => setCallMember(null)}
        onMarkReminded={handleMarkReminded}
      />

      <AddMemberModal
        isOpen={addOpen}
        groupKey={rawGroupKey}
        onDismiss={() => setAddOpen(false)}
        onAdded={() => {
          setAddOpen(false);
          present(t("suivi_add_member_done"));
          fetchDetail();
        }}
      />

      {/* Remove confirm — only reachable through a `deletable` member row. */}
      <IonModal
        isOpen={removeTarget !== null}
        className="resonance-modal"
        backdropDismiss
        onWillDismiss={() => setRemoveTarget(null)}
      >
        <ModalCard>
          <ModalTitle>{t("suivi_remove_member_title")}</ModalTitle>
          <ModalDescription>
            {t("suivi_remove_member_text", {
              // Ordinal label (+ name if one appeared): a destructive confirm must
              // say WHICH « Enfant » it is about, exactly like the row that was tapped.
              role: removeTarget
                ? memberDisplayName(removeTarget)
                : t("suivi_member_generic"),
            })}
          </ModalDescription>
          <ModalActions>
            <ModalQuietButton onClick={() => setRemoveTarget(null)}>
              {t("common_cancel")}
            </ModalQuietButton>
            <ModalPrimaryButton onClick={handleRemoveMember} disabled={removing}>
              {t("suivi_remove_member_confirm")}
            </ModalPrimaryButton>
          </ModalActions>
        </ModalCard>
      </IonModal>

      <MemberActionsModal
        isOpen={actionsMember !== null}
        member={actionsMember}
        memberName={actionsMember ? memberDisplayName(actionsMember) : ""}
        onDismiss={() => setActionsMember(null)}
        onRemove={() => {
          const target = actionsMember;
          setActionsMember(null);
          setRemoveTarget(target);
        }}
        onExit={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setExitTarget(target);
        }}
        onRecordDeath={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setDeathTarget(target);
        }}
        onClearDeath={() => {
          if (actionsMember) void handleClearDeath(actionsMember);
        }}
        onSendAccessLink={() => {
          const target = actionsMember;
          setActionsMember(null);
          setActionError(null);
          setAccessLinkTarget(target);
        }}
      />

      <AccessLinkModal
        isOpen={accessLinkTarget !== null}
        memberName={accessLinkTarget ? memberDisplayName(accessLinkTarget) : ""}
        knownEmail={accessLinkTarget?.email ?? null}
        alreadySentAt={accessLinkTarget?.invited_at ?? null}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setAccessLinkTarget(null);
          setActionError(null);
        }}
        onConfirm={handleSendAccessLink}
      />

      <ExitModal
        isOpen={exitTarget !== null || exitDossier}
        isDossier={exitDossier}
        targetName={
          exitDossier
            ? detail?.external_id ?? ""
            : exitTarget
            ? memberDisplayName(exitTarget)
            : ""
        }
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setExitTarget(null);
          setExitDossier(false);
          setActionError(null);
        }}
        onConfirm={handleExit}
      />

      <DeathModal
        isOpen={deathTarget !== null}
        targetName={deathTarget ? memberDisplayName(deathTarget) : ""}
        saving={actionSaving}
        error={actionError}
        onDismiss={() => {
          setDeathTarget(null);
          setActionError(null);
        }}
        onConfirm={handleRecordDeath}
      />
      </>
    </ResonanceLayout>
  );
};

// ref_exit_reasons code → translation key. Unknown codes (a reason added server-side
// after this build) fall back to « Autre » rather than rendering a raw code.
const exitReasonLabelKey = (code: string): string =>
  ({
    WITHDRAWAL: "suivi_exit_reason_withdrawal",
    SCREEN_FAILURE: "suivi_exit_reason_screen_failure",
    DEATH: "suivi_deceased_tag",
    LOST_TO_FOLLOW_UP: "suivi_exit_reason_lost",
    ENROLLMENT_ERROR: "suivi_exit_reason_error",
  }[code] ?? "suivi_exit_reason_other");

const AddMemberRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: none;
  background: transparent;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.4);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.5;
  }
`;

export default GroupSpace;
